import { useCallback } from 'react';
import { useFocusEffect } from 'expo-router';

import {
  NOTIFICATION_EXTRA_ORDER_STATUS_KEY,
  OrderStatus,
} from '@sg/shared/features/notifications';

import {
  type BrazePushNotificationPayload,
  useOnBrazePushNotificationReceived,
} from '../../../../context/Braze';

// ─────────────────────────────────────────────────────────────────────────────

/**
 * A hook that sets up a listener for when a in-store completed order push
 * notification is received.
 */
export const useScanToPayNotifications = (params: Params) => {
  const { onOrderCompleted } = params;

  // ─── Helpers ─────────────────────────────────────────────────────────────────

  const onPushNotificationReceived = useCallback(
    (payload?: BrazePushNotificationPayload) => {
      const extras = payload?.extras;
      const orderStatus = extras?.[NOTIFICATION_EXTRA_ORDER_STATUS_KEY];
      const isValidNotification = orderStatus === OrderStatus.InStoreCompleted;

      // Ignore other notifications
      if (!isValidNotification) return;

      onOrderCompleted();
    },
    [onOrderCompleted],
  );

  // ─────────────────────────────────────────────────────────────────────────────

  const onNotificationReceived = useOnBrazePushNotificationReceived({
    onPushNotificationReceived,
  });

  // ─── Effects ─────────────────────────────────────────────────────────────────

  useFocusEffect(onNotificationReceived);
};

// ─── Types ───────────────────────────────────────────────────────────────────

type Params = {
  onOrderCompleted: () => void;
};
